// Q9 S3 — Patient header chrome for the VisitReport wizard.
// Replaces the SR-hash header (PAIN-CATALOG-2026-05-01.md line 172 P2):
// carer needs to know which patient they're documenting BEFORE they
// document. Surfaces patient name + visit context (scheduled date,
// visit_type if known) + SR ref as secondary identification.

import type { JSX } from "react";
import type { VisitType } from "@hcm/core";
import { formatPatientRef, formatSrRef } from "../lib/refs.js";

const VISIT_TYPE_LABELS: Record<VisitType, string> = {
  standard_wellness: "Standard wellness visit",
  extended_care:     "Extended care visit",
  companion:         "Companion visit",
  post_discharge:    "Post-discharge visit",
  emergency_followup: "Emergency follow-up visit",
};

interface PatientLike {
  _id: string;
  full_name?: string | null;
  name?: string | null;
}

interface SrLike {
  _id: string;
  nsofar_sr_id?: string | null;
  scheduled_datetime?: string | null;
  visit_type?: VisitType | null;
}

interface Props {
  patient: PatientLike;
  sr: SrLike;
}

function formatScheduled(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // "Tue Apr 23, 10:30 AM"
  return d.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

export function PatientHeader({ patient, sr }: Props): JSX.Element {
  const patientName = formatPatientRef(patient);
  const scheduled = formatScheduled(sr.scheduled_datetime);
  const visitTypeLabel = sr.visit_type ? VISIT_TYPE_LABELS[sr.visit_type] : null;
  const srRef = formatSrRef(sr);

  const contextBits = [visitTypeLabel, scheduled].filter(Boolean).join(" • ");

  return (
    <header className="mb-5">
      <h1 className="text-xl font-semibold text-slate-900">{patientName}</h1>
      {contextBits && (
        <p className="mt-1 text-sm text-slate-600">{contextBits}</p>
      )}
      <p className="mt-1 text-xs text-slate-400 font-mono">SR ref: {srRef}</p>
    </header>
  );
}
